import { useState, useEffect } from 'react'
import { Routes, Route } from 'react-router-dom'
import Layout from './components/Layout'
import api from './services/api'
import HomePage from './pages/HomePage'
import BookingPage from './pages/BookingPage'
import ConfirmationPage from './pages/ConfirmationPage'
import CancelPage from './pages/CancelPage'
import LegalsPage from './pages/LegalsPage'
import PreConsultFormPage from './pages/PreConsultFormPage'
import PreConsultPage from './pages/PreConsultPage'

// Admin pages
import LoginPage from './pages/admin/LoginPage'
import ForgotPasswordPage from './pages/admin/ForgotPasswordPage'
import ResetPasswordPage from './pages/admin/ResetPasswordPage'
import AdminLayout from './pages/admin/AdminLayout'
import DashboardPage from './pages/admin/DashboardPage'
import SlotsPage from './pages/admin/SlotsPage'
import AppointmentsPage from './pages/admin/AppointmentsPage'
import PatientsPage from './pages/admin/PatientsPage'
import SmsPage from './pages/admin/SmsPage'
import PatientDetailsPage from './pages/admin/PatientDetailsPage'
import DietTemplates from './pages/admin/DietTemplates'
import LibraryPage from './pages/admin/LibraryPage'
import SettingsPage from './pages/admin/SettingsPage'
import AccountingPage from './pages/admin/AccountingPage'
import NetworkPage from './pages/admin/NetworkPage'
import SharedItemPage from './pages/SharedItemPage'
import SuperAdminDashboard from './pages/admin/SuperAdminDashboard'

// Registering new cabinet page
import RegisterPage from './pages/admin/RegisterPage'

// Patient Portal pages
import PatientPortalLayout from './pages/portal/PatientPortalLayout'
import PortalDashboardPage from './pages/portal/PortalDashboardPage'
import PortalCarnetPage from './pages/portal/PortalCarnetPage'
import PortalVisioPage from './pages/portal/PortalVisioPage' 
import PortalActivityPage from './pages/portal/PortalActivityPage'


function App() {
  const[tenantReady, setTenantReady] = useState(false)
  const [tenantError, setTenantError] = useState(null)

  useEffect(() => {
    setTenantReady(false) // <-- PATCH HMR EN DEV !
    // 1. Détection du sous-domaine
    const hostname = window.location.hostname
    //let slug = 'laetitia-lacroix' // Fallback pour Dev (IP locale)
    let slug = 'marco' // Fallback pour Dev (IP locale)
    
    const isIpOrLocalhost = /^[0-9.]+$/.test(hostname) || hostname === 'localhost'
    
    if (!isIpOrLocalhost) {
        const saasDomain = import.meta.env.VITE_SAAS_DOMAIN || 'bellablue.contact'

        // Si le domaine se termine bien par ".bellablue.contact" (ex: cabinet-jml.bellablue.contact)
        if (hostname.endsWith(`.${saasDomain}`)) {
            // On extrait le slug en retirant le domaine principal de la chaîne
            slug = hostname.replace(`.${saasDomain}`, '')
        }
    }

  // 2. Résolution via le Backend (Support de l'Apex Domain)
  api.get(`/api/workspaces/resolve?slug=${slug}&domain=${hostname}`)
      .then(res => {
          if (res.data.is_saas_root) {
              // On retire l'en-tête d'isolation pour naviguer sur la racine du SaaS commercial
              delete api.defaults.headers.common['X-Workspace-Id'];
              document.title = "BellaBlue - Le logiciel tout-en-un pour Diététiciens & Nutritionnistes";
          } else {
              api.defaults.headers.common['X-Workspace-Id'] = res.data.id;
              document.title = `${res.data.name} - Prise de RDV`;
          }
          setTenantReady(true)
      })
      .catch(err => {
          // console.error(err.config?.url, err.response?.status)
          setTenantError("Cabinet introuvable. Veuillez vérifier l'adresse web.")
      })
  },[])
  
  // 3. Écran de blocage si le sous-domaine n'existe pas
  if (tenantError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white p-8 rounded-2xl shadow-xl text-center max-w-md w-full border border-gray-100">
          <div className="w-16 h-16 bg-red-100 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl">⚠️</div>
          <h1 className="text-xl font-bold text-gray-800 mb-2">Cabinet introuvable</h1>
          <p className="text-gray-500">{tenantError}</p>
        </div>
      </div>
    )
  }

  // 4. Écran de chargement (le temps de vérifier au backend)
  if (!tenantReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="spinner border-sage-600 w-8 h-8" />
      </div>
    )
  }

  // 5. Tout est bon, on affiche l'application !
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<Layout />}>
        <Route index element={<HomePage />} />
        <Route path="booking" element={<BookingPage />} />
        <Route path="confirmation/:token" element={<ConfirmationPage />} />
        <Route path="cancel/:token" element={<CancelPage />} />
        <Route path="form/:token" element={<PreConsultFormPage />} />
        <Route path="pre-consult/:token" element={<PreConsultPage />} />
        <Route path="mentions-legales" element={<LegalsPage />} />
        <Route path="shared/:token" element={<SharedItemPage />} />
        <Route path="/admin/register" element={<RegisterPage />} />
      </Route>

      {/* Routes Portail Patient */}
      <Route path="/p/:token" element={<PatientPortalLayout />}>
          <Route index element={<PortalDashboardPage />} />
          <Route path="carnet" element={<PortalCarnetPage />} />
          <Route path="activite" element={<PortalActivityPage />} />
          <Route path="visio" element={<PortalVisioPage />} />
          <Route path="booking" element={<BookingPage />} />
      </Route>

      {/* Admin routes */}
      <Route path="/admin/login" element={<LoginPage />} />
      <Route path="/admin/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/admin/reset-password/:token" element={<ResetPasswordPage />} />
      <Route path="/admin" element={<AdminLayout />}>
      <Route index element={<DashboardPage />} />
      <Route path="slots" element={<SlotsPage />} />
      <Route path="appointments" element={<AppointmentsPage />} />
      <Route path="patients" element={<PatientsPage />} />
      <Route path="patients/:id" element={<PatientDetailsPage />} />
      <Route path="diet-templates" element={<DietTemplates />} />
      <Route path="library" element={<LibraryPage />} />
      <Route path="network" element={<NetworkPage />} />
      <Route path="accounting" element={<AccountingPage />} />
      <Route path="sms" element={<SmsPage />} />
      <Route path="settings" element={<SettingsPage />} />
      <Route path="superadmin" element={<SuperAdminDashboard />} />
      </Route>
    </Routes>
  )
}

export default App
